import { useEffect, useId, useState, type CSSProperties } from "react";
import { useOutletContext } from "react-router-dom";
import {
  adminGetAppSettings,
  adminListRecipeStandards,
  adminUpdateAppSettings,
  adminUpdateRecipeStandards,
  type AppSettings,
  type RecipeStandardKind,
  type RecipeStandardRow,
} from "../../lib/api";
import type { SessionUser } from "../../types/auth";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

/**
 * Admin Settings — currently scoped to the Recipe Standards (glass / ice /
 * method) the Recipe Editor pulls into its dropdowns. Expand later for
 * service-day cutover, max loaded drinks, etc.
 *
 * Auth: bar_manager+ (matches the underlying edge function).
 */

type Lists = Record<RecipeStandardKind, RecipeStandardRow[]>;

const KIND_LABELS: Record<RecipeStandardKind, string> = {
  glass: "Glassware",
  ice: "Ice",
  method: "Method",
};

const KIND_DESCRIPTIONS: Record<RecipeStandardKind, string> = {
  glass: "Drag-free reorder by changing the order in the list. Recipe Editor sources its glass dropdown here.",
  ice: "Single source of truth for the Recipe Editor ice dropdown.",
  method: "Build technique. Recipe Editor sources its method dropdown here.",
};

export default function AdminSettings() {
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");

  const [lists, setLists] = useState<Lists | null>(null);
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<RecipeStandardKind | null>(null);
  const [savedAt, setSavedAt] = useState<RecipeStandardKind | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [r1, r2] = await Promise.all([
        adminListRecipeStandards(),
        adminGetAppSettings(),
      ]);
      setLists(r1.standards);
      setAppSettings(r2.settings);
    } catch (err: any) {
      setError(err?.message ?? "Could not load settings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function setKindList(kind: RecipeStandardKind, rows: RecipeStandardRow[]) {
    setLists((prev) => (prev ? { ...prev, [kind]: rows } : prev));
  }

  async function saveKind(kind: RecipeStandardKind) {
    if (!lists) return;
    setSaving(kind);
    setError(null);
    try {
      const payload = lists[kind].map((r, i) => ({ value: r.value, sort_order: i + 1 }));
      const res = await adminUpdateRecipeStandards(kind, payload);
      setKindList(kind, res.values);
      setSavedAt(kind);
      setTimeout(() => setSavedAt((cur) => (cur === kind ? null : cur)), 2000);
    } catch (err: any) {
      setError(err?.message ?? "Save failed");
    } finally {
      setSaving(null);
    }
  }

  return (
    <section style={styles.shell}>
      <OperationsStudioTabs activeTab="settings" />
      <p style={styles.eyebrow}>Settings</p>
      <h1 style={styles.title}>Settings</h1>
      <p style={styles.body}>
        Recipe standards drive the editor's glass / ice / method dropdowns. Operational settings tune
        Station's service-day cutover, multiplier limits, and behavior toggles. Owner-only fields are
        read-only for non-owners.
      </p>

      {loading && <p style={styles.muted}>Loading settings…</p>}
      {error && <div style={styles.error}>{error}</div>}

      {isOwner && <ForceUpdateCard />}

      {appSettings && (
        <OperationalSettingsCard
          settings={appSettings}
          isOwner={isOwner}
          onSaved={(next) => setAppSettings(next)}
        />
      )}

      <h2 style={styles.subTitle}>Recipe standards</h2>
      {lists && (
        <div style={styles.grid}>
          {(Object.keys(KIND_LABELS) as RecipeStandardKind[]).map((kind) => (
            <KindCard
              key={kind}
              kind={kind}
              label={KIND_LABELS[kind]}
              description={KIND_DESCRIPTIONS[kind]}
              rows={lists[kind]}
              onChange={(rows) => setKindList(kind, rows)}
              onSave={() => saveKind(kind)}
              saving={saving === kind}
              justSaved={savedAt === kind}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ForceUpdateCard() {
  const [busy, setBusy] = useState(false);
  const [busyMandatory, setBusyMandatory] = useState(false);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [sentMandatoryAt, setSentMandatoryAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function push(mandatory = false) {
    if (mandatory) setBusyMandatory(true);
    else setBusy(true);
    setErr(null);
    try {
      const { adminBroadcastReload } = await import("../../lib/api");
      await adminBroadcastReload(mandatory ? "mandatory_reload" : "force_update_check");
      if (mandatory) setSentMandatoryAt(Date.now());
      else setSentAt(Date.now());
    } catch (e: any) {
      setErr(e?.message ?? "Could not send broadcast");
    } finally {
      setBusy(false);
      setBusyMandatory(false);
    }
  }

  const justSent = sentAt && Date.now() - sentAt < 4000;
  const justSentMandatory = sentMandatoryAt && Date.now() - sentMandatoryAt < 4000;

  return (
    <div style={styles.opsCard} role="group" aria-labelledby="force-update-heading">
      <div style={styles.cardHeader}>
        <h2 id="force-update-heading" style={styles.cardTitle}>Station Broadcast & Remote Reload</h2>
        <span style={styles.kindTag}>realtime broadcast</span>
      </div>
      <p style={styles.cardDesc}>
        Coordinates instant version synchronization across all mounted station tablets, host stands, and staff mobile devices.
      </p>
      {err && <div style={styles.error} role="alert">{err}</div>}
      <div style={{ ...styles.cardFooter, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => push(false)}
          disabled={busy || busyMandatory}
          style={busy ? styles.saveBtnBusy : styles.saveBtn}
        >
          {busy ? "Sending…" : justSent ? "✓ Prompt Sent" : "Push Update Prompt (Toast)"}
        </button>
        <button
          type="button"
          onClick={() => push(true)}
          disabled={busy || busyMandatory}
          style={{
            ...(busyMandatory ? styles.saveBtnBusy : styles.saveBtn),
            background: "#E05040",
            borderColor: "#E05040",
            color: "#FFFFFF",
          }}
        >
          {busyMandatory ? "Broadcasting…" : justSentMandatory ? "✓ Mandatory Refresh Sent" : "⚡ Push Mandatory Refresh to All"}
        </button>
      </div>
    </div>
  );
}

interface OperationalSettingsCardProps {
  settings: AppSettings;
  isOwner: boolean;
  onSaved: (next: AppSettings) => void;
}

function OperationalSettingsCard({ settings, isOwner, onSaved }: OperationalSettingsCardProps) {
  const [resetHour, setResetHour] = useState(String(settings.service_day_reset_hour));
  const [maxDrinks, setMaxDrinks] = useState(String(settings.max_loaded_drinks));
  const [updatedDays, setUpdatedDays] = useState(String(settings.recently_updated_window_days));
  const [defaultFilter, setDefaultFilter] = useState(settings.default_station_filter);
  const [allowOffline, setAllowOffline] = useState(settings.allow_offline_cache);
  const [requireSummary, setRequireSummary] = useState(settings.require_change_summary_on_approval);
  const [requireType, setRequireType] = useState(settings.require_issue_type_on_report);
  const [auto86, setAuto86] = useState(settings.auto_86_on_unavailable_ingredient);
  const [notifyEmail, setNotifyEmail] = useState(settings.issue_notification_email ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const resetHourId = useId();
  const maxDrinksId = useId();
  const updatedDaysId = useId();
  const defaultFilterId = useId();
  const allowOfflineId = useId();
  const requireSummaryId = useId();
  const requireTypeId = useId();
  const auto86Id = useId();
  const notifyEmailId = useId();

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const r = await adminUpdateAppSettings({
        service_day_reset_hour: Number(resetHour),
        max_loaded_drinks: Number(maxDrinks),
        recently_updated_window_days: Number(updatedDays),
        default_station_filter: defaultFilter,
        allow_offline_cache: allowOffline,
        require_change_summary_on_approval: requireSummary,
        require_issue_type_on_report: requireType,
        auto_86_on_unavailable_ingredient: auto86,
        issue_notification_email: notifyEmail.trim() || null,
      });
      onSaved(r.settings);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e: any) {
      setErr(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.opsCard} role="group" aria-labelledby="ops-heading">
      <div style={styles.cardHeader}>
        <h2 id="ops-heading" style={styles.cardTitle}>Operational</h2>
        <span style={styles.kindTag}>app_settings</span>
      </div>
      <p style={styles.cardDesc}>
        Service-day cutover, multiplier limits, default Station filter, and behavior toggles.
        {!isOwner && " Owner-only — non-owners see read-only values."}
      </p>

      <div style={styles.opsGrid}>
        <div style={styles.opsField}>
          <label htmlFor={resetHourId} style={styles.opsLabel}>Service-day reset hour (0-23)</label>
          <input
            id={resetHourId}
            type="number"
            min={0}
            max={23}
            value={resetHour}
            onChange={(e) => setResetHour(e.target.value)}
            disabled={!isOwner || busy}
            style={styles.opsInput}
          />
        </div>
        <div style={styles.opsField}>
          <label htmlFor={maxDrinksId} style={styles.opsLabel}>Max loaded drinks (1-6)</label>
          <input
            id={maxDrinksId}
            type="number"
            min={1}
            max={6}
            value={maxDrinks}
            onChange={(e) => setMaxDrinks(e.target.value)}
            disabled={!isOwner || busy}
            style={styles.opsInput}
          />
        </div>
        <div style={styles.opsField}>
          <label htmlFor={updatedDaysId} style={styles.opsLabel}>Recently-updated window (days, 1-365)</label>
          <input
            id={updatedDaysId}
            type="number"
            min={1}
            max={365}
            value={updatedDays}
            onChange={(e) => setUpdatedDays(e.target.value)}
            disabled={!isOwner || busy}
            style={styles.opsInput}
          />
        </div>
        <div style={styles.opsField}>
          <label htmlFor={defaultFilterId} style={styles.opsLabel}>Default Station filter</label>
          <select
            id={defaultFilterId}
            value={defaultFilter}
            onChange={(e) => setDefaultFilter(e.target.value as AppSettings["default_station_filter"])}
            disabled={!isOwner || busy}
            style={styles.opsInput}
          >
            <option value="today">Today</option>
            <option value="popular">Popular</option>
            <option value="all">All</option>
            <option value="cocktails">Cocktails</option>
            <option value="old_fashioned">Old Fashioned</option>
          </select>
        </div>
      </div>

      <div style={styles.opsToggles}>
        <label style={styles.opsCheckRow}>
          <input
            id={allowOfflineId}
            type="checkbox"
            checked={allowOffline}
            onChange={(e) => setAllowOffline(e.target.checked)}
            disabled={!isOwner || busy}
          />
          <span>Allow offline cache (PWA)</span>
        </label>
        <label style={styles.opsCheckRow}>
          <input
            id={requireSummaryId}
            type="checkbox"
            checked={requireSummary}
            onChange={(e) => setRequireSummary(e.target.checked)}
            disabled={!isOwner || busy}
          />
          <span>Require change summary on approval</span>
        </label>
        <label style={styles.opsCheckRow}>
          <input
            id={requireTypeId}
            type="checkbox"
            checked={requireType}
            onChange={(e) => setRequireType(e.target.checked)}
            disabled={!isOwner || busy}
          />
          <span>Require issue type on report</span>
        </label>
        <label style={styles.opsCheckRow}>
          <input
            id={auto86Id}
            type="checkbox"
            checked={auto86}
            onChange={(e) => setAuto86(e.target.checked)}
            disabled={!isOwner || busy}
          />
          <span>
            Auto-86 affected drinks when an ingredient becomes unavailable
            <br />
            <small style={{ color: "var(--muted)", fontSize: 11 }}>
              Off (recommended): the cascade-86 checkbox in the impact modal
              starts unchecked; you opt in per toggle. On: cascade defaults to
              checked + future bottle-inventory sync may auto-cascade.
            </small>
          </span>
        </label>
      </div>

      <div style={styles.opsField}>
        <label htmlFor={notifyEmailId} style={styles.opsLabel}>Notification email for new issues (optional)</label>
        <input
          id={notifyEmailId}
          type="email"
          value={notifyEmail}
          onChange={(e) => setNotifyEmail(e.target.value)}
          placeholder="alerts@sentinelroom.com"
          disabled={!isOwner || busy}
          style={styles.opsInput}
        />
      </div>

      {err && <div style={styles.error} role="alert">{err}</div>}

      {isOwner && (
        <div style={styles.cardFooter}>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            style={busy ? styles.saveBtnBusy : styles.saveBtn}
          >
            {busy ? "Saving…" : savedFlash ? "✓ Saved" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
}

interface KindCardProps {
  kind: RecipeStandardKind;
  label: string;
  description: string;
  rows: RecipeStandardRow[];
  onChange: (rows: RecipeStandardRow[]) => void;
  onSave: () => void;
  saving: boolean;
  justSaved: boolean;
}

function KindCard({ kind, label, description, rows, onChange, onSave, saving, justSaved }: KindCardProps) {
  const [draft, setDraft] = useState("");

  function addValue() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (rows.some((r) => r.value.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    const newRow: RecipeStandardRow = {
      id: `temp-${Date.now()}`,
      kind,
      value: trimmed,
      sort_order: rows.length + 1,
    };
    onChange([...rows, newRow]);
    setDraft("");
  }

  function moveUp(idx: number) {
    if (idx <= 0) return;
    const next = [...rows];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  }
  function moveDown(idx: number) {
    if (idx >= rows.length - 1) return;
    const next = [...rows];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    onChange(next);
  }
  function remove(idx: number) {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>{label}</h2>
        <span style={styles.kindTag}>{kind}</span>
      </div>
      <p style={styles.cardDesc}>{description}</p>

      <ol style={styles.list}>
        {rows.map((r, i) => (
          <li key={r.id || `${r.value}-${i}`} style={styles.row}>
            <span style={styles.rowIdx}>{i + 1}.</span>
            <span style={styles.rowValue}>{r.value}</span>
            <div style={styles.rowActions}>
              <button
                type="button"
                onClick={() => moveUp(i)}
                disabled={i === 0}
                title="Move up"
                aria-label="Move up"
                style={i === 0 ? styles.iconBtnDisabled : styles.iconBtn}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => moveDown(i)}
                disabled={i === rows.length - 1}
                title="Move down"
                aria-label="Move down"
                style={i === rows.length - 1 ? styles.iconBtnDisabled : styles.iconBtn}
              >
                ▼
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                title="Remove"
                aria-label="Remove"
                style={styles.removeBtn}
              >
                ✕
              </button>
            </div>
          </li>
        ))}
        {rows.length === 0 && (
          <li style={{ ...styles.row, ...styles.empty }}>No values yet. Add one below.</li>
        )}
      </ol>

      <div style={styles.addRow}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addValue();
            }
          }}
          placeholder={`Add ${kind}…`}
          style={styles.input}
        />
        <button type="button" onClick={addValue} style={styles.addBtn}>
          Add
        </button>
      </div>

      <div style={styles.cardFooter}>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={saving ? styles.saveBtnBusy : styles.saveBtn}
        >
          {saving ? "Saving…" : justSaved ? "✓ Saved" : "Save"}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  subTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "24px 0 12px",
    color: "var(--text)",
  },
  opsCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 12,
  },
  opsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
    gap: 12,
  },
  opsField: { display: "grid", gap: 6 },
  opsLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  opsInput: {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: "inherit",
  },
  opsToggles: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 0",
  },
  opsCheckRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    fontSize: 13,
    color: "var(--text)",
    cursor: "pointer",
  },
  shell: { maxWidth: 1080, padding: "8px 0 32px" },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 12px",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  body: { color: "var(--muted)", fontSize: 14, lineHeight: 1.5, margin: "0 0 16px" },
  muted: { color: "var(--muted)", fontSize: 13 },
  error: {
    margin: "8px 0",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
    marginTop: 8,
  },
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  cardTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    margin: 0,
    color: "var(--text)",
  },
  kindTag: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10,
    color: "var(--muted)",
    background: "var(--bg)",
    padding: "2px 6px",
    borderRadius: 4,
    border: "1px solid var(--border)",
  },
  cardDesc: { color: "var(--muted)", fontSize: 12, margin: 0, lineHeight: 1.4 },
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 8px",
    fontSize: 13,
  },
  rowIdx: {
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    minWidth: 24,
  },
  rowValue: { color: "var(--text)", flex: 1 },
  rowActions: { display: "flex", gap: 6 },
  // WCAG 2.5.5 — 40×40 touch targets.
  iconBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    width: 40,
    height: 40,
    fontSize: 12,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnDisabled: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--muted)",
    width: 40,
    height: 40,
    fontSize: 12,
    opacity: 0.4,
    cursor: "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  removeBtn: {
    background: "transparent",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    color: "#FF8A7A",
    width: 40,
    height: 40,
    fontSize: 12,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  empty: {
    color: "var(--muted)",
    fontStyle: "italic",
    justifyContent: "center",
    fontSize: 12,
  },
  addRow: { display: "flex", gap: 6 },
  input: {
    flex: 1,
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  },
  addBtn: {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  cardFooter: { display: "flex", justifyContent: "flex-end", marginTop: 4 },
  saveBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  saveBtnBusy: {
    background: "rgba(249,175,54,0.4)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "wait",
  },
};
